/**
 * The sign-in form's bot check (admin 02 §2 step 1, arch-06 §7).
 *
 * The server says whether there is one: `GET /v1/auth/admin/login-config` names a Turnstile site
 * key only where `/login` verifies the token, so the form draws the check exactly when the server
 * will ask for it — never a widget nobody checks, never a demand nobody can answer. No key means
 * no script is loaded at all.
 *
 * The widget runs `interaction-only`: it takes no space unless Cloudflare wants a human to click,
 * which is why the login design needs no slot for it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../api/client';
import type { components } from '../../api/generated/schema';

type LoginConfig = components['schemas']['AdminLoginConfigOut'];

export const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileOptions {
  sitekey: string;
  appearance: 'interaction-only';
  callback: (token: string) => void;
  'expired-callback': () => void;
  'error-callback': () => void;
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/** One script tag per page, however many times the form mounts. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('no turnstile')));
    script.onerror = () => {
      loading = null;
      reject(new Error('turnstile did not load'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** For tests: forget the cached load so each case starts from a page that has not loaded it. */
export function forgetTurnstileForTest(): void {
  loading = null;
}

export interface BotCheck {
  /** The server will refuse a sign-in without a solved check. */
  required: boolean;
  /** The solved token, or null while unsolved, expired or failed. */
  token: string | null;
  /** The script would not load — said on the form, because Continue cannot work without it. */
  unavailable: boolean;
  container: React.RefObject<HTMLDivElement | null>;
  /** Tokens are single-use: a refused sign-in spends it, so the next attempt needs a new one. */
  reset: () => void;
}

export function useBotCheck(): BotCheck {
  const container = useRef<HTMLDivElement | null>(null);
  const widget = useRef<{ api: TurnstileApi; id: string } | null>(null);
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let live = true;
    apiFetch<LoginConfig>('/v1/auth/admin/login-config')
      .then((config) => live && setSiteKey(config.turnstile_site_key ?? null))
      /* No config, no widget. If the server does verify, its refusal names the bot check — the
         form does not guess at a requirement it could not read. */
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!siteKey) return;
    let live = true;
    loadTurnstile().then(
      (api) => {
        /* v8 ignore next -- the container is always mounted by the time the script loads. */
        if (!live || !container.current) return;
        const id = api.render(container.current, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          callback: setToken,
          'expired-callback': () => setToken(null),
          'error-callback': () => setToken(null),
        });
        widget.current = { api, id };
      },
      () => live && setUnavailable(true),
    );
    return () => {
      live = false;
      if (widget.current) widget.current.api.remove(widget.current.id);
      widget.current = null;
    };
  }, [siteKey]);

  const reset = useCallback(() => {
    setToken(null);
    if (widget.current) widget.current.api.reset(widget.current.id);
  }, []);

  return { required: siteKey !== null, token, unavailable, container, reset };
}
